<template>
	<div class="popular-filter-bar">
		<div class="popular-filter-bar__title">
			<h2 class="title section-title">{{ tabTitle }}</h2>
		</div>
		<div class="popular-filter-bar__main-filters">
			<div class="actions" :class="{ 'search-bar-active': isSearchBarVisible }">
				<div class="search-bar" v-if="isSearchBarVisible">
					<SearchbarInput
						ref="searchbar"
						class="searchbar"
						autocorrect="off"
						autocomplete="off"
						:placeholder="$t('WEBAPP_SEARCH_PLACEHOLDER')"
						:cancel-button-text="$t('WEBAPP_DONE')"
						:modelValue="searchText"
						:debounce="300"
						@onChange="change"
						@onClear="clear"
						@onBlur="cancel"
					/>
				</div>

				<div class="other-actions">
					<BasicButton class="search" v-if="!isSearchBarVisible" @click="showSearchBar">
						<FontAwesomeIcon :icon="faSearch" />
					</BasicButton>
					<ChipButton class="all-filters">
						<button @click.prevent="toggleFilterBar">
							<FontAwesomeIcon :icon="faFilter" />
							<span>{{ $t('WEBAPP_ALL_FILTERS') }}</span>
							<div class="circle" v-if="activeFiltersCount">{{ activeFiltersCount }}</div>
						</button>
					</ChipButton>
					<FilterButtonSeo class="all-filters-mobile" :collectionType="collectionType.POPULAR">
						<div class="circle" v-if="activeFiltersCount">{{ activeFiltersCount }}</div>
					</FilterButtonSeo>

					<TitleListSortingSwitcherSeo class="sorting-switcher" :collectionType="collectionType.POPULAR" />

					<ResetButton
						class="reset-button"
						:keepProvidersState="currentFilters.providers.length === 1"
						:keepContentTypeState="popularPageExpActive"
					/>
				</div>
			</div>
		</div>
		<FilterBarSeo
			v-if="showFilterBar"
			:collectionType="collectionType.POPULAR"
			:currentFilters="currentFilters"
			:onlyShowFilters="true"
			:keepProvidersState="true"
			:isProviderPage="isProviderPage"
			:popularPageExpActive="popularPageExpActive"
			:class="{ 'filter-bar-popular': true }"
		>
		</FilterBarSeo>
		<div class="popular-filter-bar__second-filters">
			<TitlesCount :count="titleCount" />
			<slot></slot>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import ChipButton from '@/ui-components/ChipButton.vue';
import SearchbarInput from '@/ui-components/SearchbarInput.vue';
import FilterButtonSeo from '@/components/filter-bar/seo/FilterButtonSeo.vue';
import TitleListSortingSwitcherSeo from '@/components/filter-bar/seo/TitleListSortingSwitcherSeo.vue';
import { CollectionType } from '@/enums/collection-type';
import { FilterValues } from '@/helpers/filter-helper';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { PopularTabType } from '@/interfaces/titles';
import Provider from '@/interfaces/provider';
import ResetButton from '@/components/buttons/ResetButton.vue';
import { getSelectedTab } from './helper';
import TitlesCount from '@/components/filter-bar/TitlesCount.vue';
import { activeSubgenreCountries } from '@/constants/subgenres';
import { faFilter, faSearch } from '@fortawesome/free-solid-svg-icons';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const FilterBarSeo = defineAsyncComponent(
	() => import(/* webpackChunkName: "filter-bar-seo" */ '@/components/filter-bar/seo/FilterBarSeo.vue')
);

const filterValues = ref({ ...FilterValues }); // Initially clone the FilterValues object

const collectionType = ref(CollectionType);
const showFilterBar = ref(false);
const searchBarVisible = ref(false);

const searchbar = ref<{ setFocus: () => void } | null>(null);

const props = withDefaults(
	defineProps<{
		titleCount: number;
		currentFilters: FilterCollection;
		selectedTab?: PopularTabType;
		provider: Provider;
		searchText: string;
		isProviderPage?: boolean;
		// POPULAR_EXP
		popularPageExpActive?: boolean;
		// POPULAR_EXP
		activeSubgenreTitle?: string;
	}>(),
	{
		isProviderPage: false,
		popularPageExpActive: false,
	}
);

const emit = defineEmits<{
	(e: 'search', value: string): void;
}>();

const { activeRoute } = storeToRefs(useRoutingStore());
const { t } = useI18n();
const { ageCertifications } = storeToRefs(useConstantStore());
const { country } = storeToRefs(useLanguageStore());

watch(
	() => activeRoute.value,
	(newValue, oldValue) => {
		if (
			newValue?.name?.startsWith('app.titles.popular') &&
			oldValue?.name?.startsWith('app.titles.popular') &&
			newValue.name !== oldValue.name
		) {
			searchBarVisible.value = false;
			emit('search', '');
		}
	}
);

const isSearchBarVisible = computed(() => {
	return searchBarVisible.value || !!props.searchText;
});

watch(
	() => ageCertifications.value.hasCertifications,
	newValue => {
		if (!newValue) {
			// Remove the age certification filter dynamically if certifications are not available
			delete filterValues.value['FilterAgeCertification'];
		}
	}
);

const activeFiltersCount = computed(() => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { providers, content_types, sort_by, sorting_random_seed, sort_asc, ...filters } = props.currentFilters;
	return Object.keys(filters).filter((key, index, arr) => {
		const value = filters[key as keyof typeof filters];
		if (
			key.startsWith('release_year') &&
			!!value &&
			index !== arr.findIndex(k => k.startsWith('release_year') && !!filters[k as keyof typeof filters])
		) {
			return false;
		}
		return Array.isArray(value) ? value.length > 0 : !!value;
	}).length;
});

const tabTitle = computed(() => {
	// POPULAR_EXP
	if (!props.provider) {
		if (props.selectedTab == PopularTabType.MOVIE) {
			return 'All movies streaming online';
		} else if (props.selectedTab == PopularTabType.SHOW) {
			return 'All TV shows streaming online';
		} else {
			return '';
		}
	}

	if (
		activeSubgenreCountries.includes(country.value) &&
		props.activeSubgenreTitle &&
		props.currentFilters.subgenres?.length === 1 &&
		props.currentFilters.providers?.length === 1
	) {
		if (props.selectedTab == PopularTabType.MOVIE) {
			return t('WEBAPP_SUBGENRE_MOVIES_ON_PROVIDER', {
				provider: props.provider.clearName,
				subgenre: props.activeSubgenreTitle,
			});
		} else if (props.selectedTab == PopularTabType.SHOW) {
			return t('WEBAPP_SUBGENRE_SHOWS_ON_PROVIDER', {
				provider: props.provider.clearName,
				subgenre: props.activeSubgenreTitle,
			});
		} else {
			return t('WEBAPP_SUBGENRE_ALL_ON_PROVIDER', {
				provider: props.provider.clearName,
				subgenre: props.activeSubgenreTitle,
			});
		}
	}
	// POPULAR_EXP
	switch (props.selectedTab) {
		case PopularTabType.MOVIE:
			return t('WEBAPP_ALL_MOVIES_PROVIDER', { provider: props.provider.clearName });
		case PopularTabType.SHOW:
			return t('WEBAPP_ALL_SHOWS_PROVIDER', { provider: props.provider.clearName });
		default:
			return t('WEBAPP_ALL_MOVIES_SHOWS_PROVIDER', { provider: props.provider.clearName });
	}
});

function change(evt: any) {
	emit('search', evt.target.value);
}

function clear() {
	TrackingHelper.trackEvent('search_suggest_clear', {
		action: 'clear_all',
		property: props.selectedTab,
	});
}

function cancel() {
	setTimeout(() => {
		if (props.searchText === '') {
			searchBarVisible.value = false;
		}
	}, 100);
}

function showSearchBar() {
	searchBarVisible.value = true;
	const selectedTab = getSelectedTab(activeRoute?.value?.name as string);

	TrackingHelper.trackEvent('userinteraction', {
		action: 'open_suggester',
		property: selectedTab,
	});

	setTimeout(() => {
		searchbar.value?.setFocus();
	}, 100);
}

function toggleFilterBar() {
	showFilterBar.value = !showFilterBar.value;

	const selectedTab = getSelectedTab(activeRoute?.value?.name as string);

	TrackingHelper.trackEvent('userinteraction', {
		action: 'filters_expanded',
		label: 'all',
		property: selectedTab,
	});
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

@mixin xs {
	@media (max-width: 768px) {
		@content;
	}
}

.popular-filter-bar {
	@media (max-width: 1050px) {
		padding-inline: 15px;
	}

	&__title {
		.title {
			color: #fff;
			font-size: 20px;
			font-weight: 600;
			margin-top: 15px;
			margin: 12px 0 24px 0;
		}
	}

	.filter-bar-popular {
		margin-top: 16px;

		:deep(> div) {
			padding: 0;
		}
	}

	&__main-filters {
		.sorting-switcher {
			height: 28px;
			background-color: #4c5a67;
			padding: 6px 10px;
			color: #b9bdcc;
			font-size: 15px;
			font-weight: 400;
			border-radius: 4px;

			:deep(.title-list-sorting-switcher__label) {
				display: none;
			}

			:deep(svg) {
				margin-inline-start: 8px;
			}
		}

		.all-filters {
			color: #b9bdcc;
			font-size: 15px;
			font-weight: 400;
			border-radius: 4px;
			padding: 1px 0;

			span {
				margin-inline-start: 8px;
			}

			@media (max-width: 1050px) {
				display: none;
			}
		}

		.circle {
			width: 15px;
			height: 15px;
			line-height: 15px;
			background-color: #ffff;
			color: #000;
			border-radius: 50%;
			display: inline-block;
			font-size: 12px;
			margin-inline-start: 8px;
			margin-top: -1px;
			text-align: center;
			padding-inline-end: 1px;
		}

		.all-filters-mobile {
			padding: 1px 0;

			:deep(button) {
				color: #b9bdcc;
			}

			@media (min-width: 1051px) {
				display: none;
			}
		}

		.actions {
			display: flex;
			flex-direction: row;
			@media (min-width: 1051px) {
				align-items: center;
			}

			&.search-bar-active {
				@include xs() {
					flex-direction: column;
				}
			}

			.search-bar {
				width: 50%;
				@include xs() {
					width: 90%;
					margin-bottom: 16px;
					border-radius: 7px;
					overflow: hidden;
				}
				margin-inline-end: 8px;

				:deep(input::-webkit-search-cancel-button) {
					-webkit-appearance: none;
				}

				:deep(input) {
					background-color: #10161d;
				}
			}

			.other-actions {
				display: flex;
				flex-direction: row;
				align-items: center;
				height: fit-content;
				flex: 1;
				line-height: 18px;

				> * {
					margin-inline-end: 8px;
					background-color: #303740;
					border: none;
					height: 100%;

					&.reset-button {
						background-color: transparent;
						margin-inline-start: auto;
						display: none;
						@media (max-width: 1050px) {
							display: block;
						}
					}
				}
			}
		}

		.search {
			:deep(.basic-button__content) {
				height: 18px;
			}

			font-size: 18px;
			color: #c6c8cd;
			padding: 6px 15px;
		}
	}

	&__second-filters {
		display: flex;
		flex-direction: row;
		gap: 16px;
		margin: 16px 0;
		align-items: center;
		@include xs() {
			margin: 24px 0;
		}

		@include xs() {
			:deep(.hidden-horizontal-scrollbar) {
				margin-left: -15px;
				margin-right: 0;
			}
		}

		.genre-item {
			padding: 8px 16px;
			background: #111924;
			margin-inline-end: 8px;
			border-radius: 8px;
			cursor: pointer;

			&.active {
				background: $jw-color-gold;
				color: #fff;
			}
		}
	}
}

.searchbar {
	padding: 0;
}
</style>
